import {
  DEFAULT_MOVE_SPEED,
  type Grid,
  type Mover,
  type Vec2,
  type Walker,
  accumulateSteps,
  createClearanceField,
  createWalker,
  findPath,
  smoothPath,
  stepWalker,
} from '@d2/core';

import { screenToCell } from './iso';

/**
 * What a click means, and what a frame does about it.
 *
 * Everything here is a *composition* of `@d2/core` — `findPath`, `smoothPath`,
 * `createWalker`, `stepWalker`, `accumulateSteps` — and deliberately nothing
 * else. Not one movement rule is restated: the server will run the identical
 * functions as truth, and a renderer that re-derived a route, a step length or a
 * half-cell offset would be a second definition of a rule that only gets to have
 * one (`ARCHITECTURE.md`). If something in here ever needs a decision core does
 * not already export, that decision belongs in core.
 *
 * It is also Phaser-free, so the whole pipeline — pixel to cell to route to the
 * position drawn twenty times a second — is unit-testable in milliseconds. The
 * scene is left with pointer events and a sprite.
 *
 * Two rules worth stating out loud:
 *
 *  - **A route that does not exist is a returned `null`, never a throw.**
 *    Clicking rock, clicking off the map and clicking a cavern with no way in
 *    are ordinary things a player does, and the honest answer to all three is
 *    that the entity does not go (`CLAUDE.md`: an unreachable path is a result).
 *  - **Time is spent in whole steps.** Frames arrive at whatever rate the
 *    browser feels like; `accumulateSteps` banks them and hands back only exact
 *    50 ms steps, so the client walks the line the server would and a stutter
 *    never turns into a different position.
 */

/**
 * The radius of the body that walks, in cells. **A placeholder** — nothing in
 * the game produces an entity size yet (TODO row 71).
 *
 * Half a cell, for two reasons that happen to agree. The placeholder capsule is
 * `ENTITY_SIZE.width` across, which projects back to a radius of about 0.29
 * cells, so 0.5 is comfortably wide enough to contain what is actually drawn —
 * the pathfinder never routes the sprite through a gap the sprite visibly does
 * not fit. And 0.5 is the largest disc a one-cell corridor admits, which at this
 * grid resolution is the widest a body can be without losing cells: every
 * walkable cell has a clearance of at least 0.5, so this radius forbids nothing
 * the map allows. The next size up a cave reliably offers is 1.5, and that needs
 * either a monster table or a finer nav grid — both open questions.
 */
export const ENTITY_RADIUS = 0.5;

/**
 * The entity's whole movement state: where it is, where it is going, and the
 * unspent milliseconds between the browser's clock and the simulation's.
 *
 * One value, replaced rather than mutated, so "what is the entity doing" has a
 * single answer and a frame cannot half-apply an update.
 */
export interface Motion {
  readonly walker: Walker;
  /** Time banked but not yet worth a whole step. Carried between frames; never reset by a new order. */
  readonly remainder: number;
}

/**
 * The disc that walks this map, and the clearance field it is measured against.
 *
 * Built **once per cave** and kept: the field is a sweep of the whole grid, so
 * rebuilding it per click would cost more than the searches it feeds
 * (`clearance.ts`). It carries its own grid, which is both how `findPath`
 * refuses a field built for another map and why nothing downstream has to be
 * handed the grid separately.
 */
export const createMover = (grid: Grid, radius = ENTITY_RADIUS): Mover => ({
  clearance: createClearanceField(grid),
  radius,
});

/** An entity standing still at a continuous position. */
export const startMotion = (position: Vec2, speed = DEFAULT_MOVE_SPEED): Motion => ({
  walker: createWalker(position, [], speed),
  remainder: 0,
});

/**
 * Orders the entity to a cell: A*, string-pulled, from wherever it currently
 * stands. `null` when there is no route, and the caller does nothing.
 *
 * The search and the smoothing take the same mover, which is what keeps a
 * pulled-straight line inside the corridor the route was found in.
 */
export const walkTo = (mover: Mover, motion: Motion, target: Vec2): Motion | null => {
  const { grid } = mover.clearance;
  const route = findPath(grid, motion.walker.position, target, mover);

  if (route === null) return null;

  return {
    ...motion,
    walker: createWalker(motion.walker.position, smoothPath(grid, route, mover), motion.walker.speed),
  };
};

/**
 * The same order, given in pixels: the click itself.
 *
 * The cell→point conversion belongs to core (`createWalker` centres every
 * waypoint) and the pixel→cell conversion belongs to `iso.ts`. This is the seam
 * between them and holds no arithmetic of its own.
 */
export const walkToPoint = (mover: Mover, motion: Motion, world: Vec2): Motion | null =>
  walkTo(mover, motion, screenToCell(world));

/**
 * Advances the walk by the whole steps `elapsedMs` has paid for.
 *
 * The frame's own duration never reaches the simulation: it is banked, split
 * into 50 ms steps and the change carried into the next frame. A long stall is
 * capped by core rather than replayed (see `MAX_CATCHUP_STEPS`).
 */
export const advance = (motion: Motion, elapsedMs: number): Motion => {
  const budget = accumulateSteps(motion.remainder, elapsedMs);
  let walker = motion.walker;

  for (let step = 0; step < budget.steps; step += 1) {
    walker = stepWalker(walker).walker;
  }

  return { walker, remainder: budget.remainder };
};

/**
 * Puts the entity down at a continuous position and cancels whatever walk was
 * under way.
 *
 * This is what a replicated position will arrive through: the server's word on
 * where an entity is overrides a route the client was part-way along, rather
 * than being blended with it.
 */
export const placeAt = (motion: Motion, position: Vec2): Motion => ({
  ...motion,
  walker: createWalker(position, [], motion.walker.speed),
});
